import { c as _c } from "react/compiler-runtime";
import { IcoChevronLeftBig16 } from '@onefootprint/icons';
import { Dialog, RadioSelect, TextInput } from '@onefootprint/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChoiceList from '../choice-list';
import { newSection, type ReportSectionKind, serializeOutline } from './report-outline';
import { KIND_GROUPS, KINDS_IN_GROUP, SECTION_KINDS } from './section-kinds';
type TaskChoice = {
  id: string;
  label: string;
  /** The task's place in the plan, numbered as the nav numbers it. */
  position: number;
};
type NewReport = {
  /** Set when the dialog asked for the task, on the Output page. */
  taskId?: string;
  name: string;
  spec: string;
  /** The first section, which takes focus once the report opens. */
  focusSectionId: string;
};
type NewReportDialogProps = {
  open: boolean;
  title: string;
  defaultName: string;
  /** Asks which task writes the report first, for a page that doesn't belong to one task. */
  tasks?: TaskChoice[];
  onClose: () => void;
  onCreate: (report: NewReport) => void;
};
type Step = 'task' | 'name' | 'section';
const NewReportDialog = t0 => {
  const $ = _c(60);
  const {
    open,
    title,
    defaultName,
    tasks,
    onClose,
    onCreate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.report"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const firstStep = tasks ? "task" : "name";
  const [step, setStep] = useState(firstStep);
  const [taskId, setTaskId] = useState();
  const [typedName, setTypedName] = useState(null);
  const [kind, setKind] = useState("narrative");
  const created = useRef(null);
  const t2 = typedName ?? defaultName;
  let t3;
  if ($[1] !== t2) {
    t3 = t2.trim();
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const name = t3;
  let t4;
  if ($[3] !== firstStep || $[4] !== onClose) {
    t4 = () => {
      setStep(firstStep);
      setTaskId(undefined);
      setTypedName(null);
      setKind("narrative");
      onClose();
    };
    $[3] = firstStep;
    $[4] = onClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const close = t4;
  let t5;
  if ($[6] !== firstStep || $[7] !== step) {
    t5 = () => setStep(step === "section" ? "name" : firstStep);
    $[6] = firstStep;
    $[7] = step;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const back = t5;
  const canGoBack = step !== firstStep;
  let t6;
  if ($[9] !== close || $[10] !== kind || $[11] !== name || $[12] !== taskId) {
    t6 = () => {
      const section = newSection(kind, "");
      created.current = {
        taskId,
        name,
        spec: serializeOutline([section]),
        focusSectionId: section.id
      };
      close();
    };
    $[9] = close;
    $[10] = kind;
    $[11] = name;
    $[12] = taskId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const create = t6;
  let t7;
  if ($[14] !== create || $[15] !== step) {
    t7 = step === "name" ? () => setStep("section") : create;
    $[14] = create;
    $[15] = step;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const next = t7;
  let t8;
  if ($[17] !== back || $[18] !== canGoBack || $[19] !== t) {
    t8 = canGoBack ? {
      ariaLabel: t(_temp),
      component: IcoChevronLeftBig16,
      onClick: back
    } : undefined;
    $[17] = back;
    $[18] = canGoBack;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== onCreate) {
    t9 = event => {
      const report = created.current;
      if (!report) {
        return;
      }
      created.current = null;
      event.preventDefault();
      onCreate(report);
    };
    $[21] = onCreate;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== name || $[24] !== next || $[25] !== step || $[26] !== t) {
    t10 = step === "task" ? undefined : {
      label: t(_temp2),
      disabled: !name,
      onClick: next
    };
    $[23] = name;
    $[24] = next;
    $[25] = step;
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== back || $[29] !== canGoBack || $[30] !== close || $[31] !== t) {
    t11 = canGoBack ? {
      label: t(_temp3),
      onClick: back
    } : {
      label: t(_temp4),
      onClick: close
    };
    $[28] = back;
    $[29] = canGoBack;
    $[30] = close;
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== step || $[34] !== t || $[35] !== tasks) {
    t12 = step === "task" && tasks && <><p className="text-body-3 text-secondary">{t(_temp5)}</p><ChoiceList choices={tasks.map(task => ({
        id: task.id,
        name: task.label,
        marker: <span className="text-caption-3 text-tertiary tabular-nums">{task.position}</span>,
        onSelect: () => {
          setTaskId(task.id);
          setStep("name");
        }
      }))} /></>;
    $[33] = step;
    $[34] = t;
    $[35] = tasks;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== defaultName || $[38] !== name || $[39] !== step || $[40] !== t || $[41] !== typedName) {
    t13 = step === "name" && <form onSubmit={event_0 => {
      event_0.preventDefault();
      if (name) {
        setStep("section");
      }
    }}><TextInput size="compact" autoFocus={true} label={t(_temp6)} onChange={event_1 => setTypedName(event_1.target.value)} value={typedName ?? defaultName} /></form>;
    $[37] = defaultName;
    $[38] = name;
    $[39] = step;
    $[40] = t;
    $[41] = typedName;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== kind || $[44] !== step || $[45] !== t) {
    t14 = step === "section" && <><p className="text-body-3 text-secondary">{t(_temp7)}</p><RadioSelect onChange={value => setKind(value as ReportSectionKind)} options={KIND_GROUPS.map(group => ({
        label: t($_6 => $_6.groups[group]),
        options: KINDS_IN_GROUP(group).map(option => ({
          value: option,
          title: t($_7 => $_7.kinds[option].label),
          description: t($_8 => $_8.kinds[option].purpose),
          IconComponent: SECTION_KINDS[option].icon
        }))
      }))} value={kind} /></>;
    $[43] = kind;
    $[44] = step;
    $[45] = t;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t12 || $[48] !== t13 || $[49] !== t14) {
    t15 = <div className="flex max-h-[min(48rem,calc(100vh-14rem))] flex-col gap-4 overflow-y-auto p-6">{t12}{t13}{t14}</div>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== close || $[52] !== open || $[53] !== t10 || $[54] !== t11 || $[55] !== t15 || $[56] !== t8 || $[57] !== t9 || $[58] !== title) {
    t16 = <Dialog headerIcon={t8} noPadding={true} onClose={close} onCloseAutoFocus={t9} open={open} primaryButton={t10} secondaryButton={t11} size="default" title={title}>{t15}</Dialog>;
    $[51] = close;
    $[52] = open;
    $[53] = t10;
    $[54] = t11;
    $[55] = t15;
    $[56] = t8;
    $[57] = t9;
    $[58] = title;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  return t16;
};
export type { NewReport };
export default NewReportDialog;
function _temp($) {
  return $.newReport.back;
}
function _temp2($_0) {
  return $_0.newReport.continue;
}
function _temp3($_1) {
  return $_1.newReport.back;
}
function _temp4($_2) {
  return $_2.newReport.cancel;
}
function _temp5($_3) {
  return $_3.newReport.chooseTask;
}
function _temp6($_4) {
  return $_4.newReport.nameLabel;
}
function _temp7($_5) {
  return $_5.newReport.chooseSection;
}